/**
 * Proposte di abbinamento fra movimenti bancari e documenti.
 *
 * **Propone soltanto.** Un abbinamento sbagliato scritto da solo è peggio di
 * nessun abbinamento: fa risultare incassata una fattura che non lo è, e chi
 * guarda i numeri non ha modo di accorgersene. Qui si producono candidature
 * che una persona conferma — la stessa scelta fatta per le regole sulla
 * dicitura e per i giroconti.
 *
 * Perché tre condizioni insieme e non una: l'importo da solo abbina fatture
 * ricorrenti dello stesso valore fra loro (il canone di marzo con quello di
 * giugno), la data da sola abbina tutto ciò che scade lo stesso giorno, e il
 * nome da solo non distingue due fatture dello stesso fornitore. È la
 * coincidenza dei tre a rendere una proposta affidabile — e anche così resta
 * una proposta.
 */
import { giorniTra } from './date.js';
const FORME_SOCIETARIE = /\b(S\.?P\.?A\.?|S\.?R\.?L\.?S?|S\.?N\.?C\.?|S\.?A\.?S\.?|SOC\.?\s*COOP\.?|SOCIETA'?|&|DI)\b/g;
/**
 * Parole che compaiono in mezzo settore e non identificano nessuno. Restano
 * come rete di sicurezza, ma il grosso del lavoro lo fa la frequenza reale nei
 * nomi dell'azienda: è un criterio che si tara da solo su chi hai davvero in
 * anagrafica, invece che su una lista decisa a tavolino.
 */
const GENERICHE = new Set([
    'ITALIA',
    'ITALIANA',
    'GROUP',
    'SERVICE',
    'SERVICES',
    'SERVIZI',
    'STUDIO',
    'ENERGIA',
    'ENERGIE',
    'RINNOVABILI',
    'IMPIANTI',
    'SISTEMI',
    'CONSULTING',
    'PARTNERS',
    'GENERAL',
    'GLOBAL',
    'TRADING',
    'UNIPERSONALE',
    'ASSICURAZIONI',
    'IMMOBILIARE',
    'COSTRUZIONI',
    'SOLUTION',
    'SOLUTIONS',
]);
/**
 * Le parole di un nome che vale la pena cercare in una causale bancaria.
 * Si tengono anche quelle di due caratteri: in «STUDIO 2B» il pezzo che
 * distingue è proprio il «2B», e scartarlo lascerebbe «STUDIO», che è come non
 * avere un nome.
 */
export function paroleNome(denominazione) {
    return denominazione
        .toUpperCase()
        .replace(FORME_SOCIETARIE, ' ')
        .replace(/[^A-Z0-9À-Ü ]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .filter((p) => p.length >= 2);
}
/**
 * Quante controparti diverse contengono ciascuna parola. «SERVICE» in cinque
 * ragioni sociali non identifica nessuno; «IFIS» in una sola sì.
 */
export function frequenzaParole(denominazioni) {
    const conta = new Map();
    const visti = new Set();
    for (const d of denominazioni) {
        if (!d || visti.has(d))
            continue;
        visti.add(d);
        for (const p of new Set(paroleNome(d)))
            conta.set(p, (conta.get(p) ?? 0) + 1);
    }
    return conta;
}
/**
 * Come agganciare un nome a una causale. Se il nome contiene una parola che
 * identifica **solo** questa controparte, quella basta. Altrimenti servono
 * tutte: «HAL SERVICE» va bene, «SERVICE» da solo no — aggancerebbe le altre
 * quattro aziende che hanno «service» nella ragione sociale.
 */
export function chiaviNome(denominazione, frequenza) {
    const parole = paroleNome(denominazione);
    const distintive = parole
        .filter((p) => p.length >= 4 && !GENERICHE.has(p) && (frequenza.get(p) ?? 1) === 1)
        .sort((a, b) => b.length - a.length);
    return { distintive, tutte: parole };
}
/**
 * La causale cita il numero del documento?
 *
 * Un numero corto e tutto cifre — «70», «83» — può capitare per caso dentro i
 * codici che le banche infilano nelle causali, e prenderlo per una citazione
 * darebbe una certezza che non c'è. Per questi si pretende che davanti ci sia
 * una parola che significa «fattura»: è quella a rendere la citazione una
 * citazione. Un numero già distintivo di suo — lungo, o con lettere e trattini
 * come «703-W» — non ha bisogno di scorta.
 */
export function numeroCitato(numero, dicituraMaiuscola) {
    const n = numero.toUpperCase().trim();
    if (n.length < 2)
        return false;
    const fuga = n.replace(/[.*+?()[\]{}|^$\\]/g, '\\$&');
    const distintivo = n.length >= 5 || /[^0-9]/.test(n);
    const confine = `(^|[^A-Z0-9])`;
    if (distintivo && new RegExp(`${confine}${fuga}([^A-Z0-9]|$)`).test(dicituraMaiuscola))
        return true;
    if (!distintivo)
        return conScorta(fuga, dicituraMaiuscola);
    /**
     * Il numero come lo scrive chi paga, non come lo scrive il gestionale.
     *
     * Le fatture di Erretielle si chiamano «1123-W», e il cliente scrive
     * «SALDO FATTURA NR. 1123»: il suffisso della serie non lo riporta nessuno.
     * Si accetta allora la parte numerica iniziale — ma solo con la scorta di
     * una parola che vuol dire fattura, come per i numeri corti, perché «1123»
     * da solo può essere qualunque codice.
     */
    const cifre = n.match(/^\d+/)?.[0];
    return cifre !== undefined && cifre !== n && cifre.length >= 2 ? conScorta(cifre, dicituraMaiuscola) : false;
}
/** Il numero preceduto da `FT`, `FAT…`, `DOC`, con o senza «N.» in mezzo. */
function conScorta(numeroInRegex, dicituraMaiuscola) {
    return new RegExp(`(FT|FAT[A-Z]*|DOC)[^A-Z0-9]{0,4}(N[R°.]?[^A-Z0-9]{0,4})?${numeroInRegex}([^A-Z0-9]|$)`).test(dicituraMaiuscola);
}
/**
 * Il pezzo di nome della controparte che la causale riporta, o `null`.
 *
 * Una parola che identifica solo questa controparte basta da sola: le banche
 * troncano i nomi in modi imprevedibili, e pretendere il nome intero farebbe
 * perdere abbinamenti buoni. Quando nessuna parola è identificante si pretende
 * il nome per intero; e se restano solo parole generiche non si aggancia
 * niente — «STUDIO» che pretende «STUDIO» non filtra nulla.
 */
export function nomeNellaCausale(controparte, dicituraMaiuscola, frequenza) {
    if (!controparte)
        return null;
    const { distintive, tutte } = chiaviNome(controparte, frequenza);
    const perDistintiva = distintive.find((n) => dicituraMaiuscola.includes(n));
    if (perDistintiva)
        return perDistintiva;
    const nomeUsabile = tutte.length > 0 && tutte.some((p) => !GENERICHE.has(p));
    return nomeUsabile && tutte.every((p) => dicituraMaiuscola.includes(p)) ? tutte.join(' ') : null;
}
/**
 * Quanti centesimi di differenza si accettano quando la causale **cita il
 * numero** della fattura.
 *
 * Con il numero scritto da chi ha pagato il dubbio su quale fattura sia non
 * c'è; resta solo l'importo, e un centesimo di arrotondamento o una piccola
 * commissione trattenuta non cambiano la risposta. Il caso vero: la 1123-W da
 * 134,20 €, pagata con un bonifico da 134,21 € che dice «SALDO FATTURA NR.
 * 1123». Senza questo margine la trovava solo chi la cercava a mano.
 *
 * Un euro: sui dati di Erretielle la differenza più grande con il numero in
 * causale è di 69 centesimi. Oltre, non è più un arrotondamento.
 */
export const TOLLERANZA_CON_NUMERO_CENT = 100;
export function proponiAbbinamenti(movimenti, documenti, opzioni = {}) {
    const tolleranza = opzioni.giorniTolleranza ?? 5;
    // Indice per importo: l'uguaglianza al centesimo è la condizione più
    // selettiva, quindi è quella da cui conviene partire.
    const perImporto = new Map();
    for (const d of documenti) {
        if (d.residuoCent <= 0)
            continue;
        const chiave = `${d.tipo}|${d.residuoCent}`;
        const lista = perImporto.get(chiave);
        if (lista)
            lista.push(d);
        else
            perImporto.set(chiave, [d]);
    }
    const frequenza = frequenzaParole(documenti.map((d) => d.controparte));
    const candidature = [];
    for (const m of movimenti) {
        const residuo = Math.abs(m.importoCent) - m.allocatoCent;
        if (residuo <= 0)
            continue;
        const tipo = m.importoCent > 0 ? 'ATTIVA' : 'PASSIVA';
        const dicitura = m.dicitura.toUpperCase();
        const trovati = [];
        for (const d of perImporto.get(`${tipo}|${residuo}`) ?? []) {
            /**
             * Due date sono legittimamente attendibili: la **scadenza** scritta sul
             * documento e la **cassa attesa**, che è la scadenza corretta col ritardo
             * osservato di quella controparte. Si prende lo scarto minore fra le due:
             * chi paga sistematicamente tre giorni prima non è in ritardo di tre
             * giorni ogni volta, è puntuale a modo suo — e misurarlo sulla data
             * sbagliata terrebbe fuori dalle proposte sicure abbinamenti che sono
             * esatti.
             */
            const nominale = d.dataScadenzaNominale ?? d.dataCassaAttesa;
            const scartoNominale = Math.abs(giorniTra(nominale, m.data));
            const scartoAtteso = Math.abs(giorniTra(d.dataCassaAttesa, m.data));
            const scarto = Math.min(scartoNominale, scartoAtteso);
            const scadenza = scartoAtteso < scartoNominale ? d.dataCassaAttesa : nominale;
            if (scarto > tolleranza)
                continue;
            const nucleo = nomeNellaCausale(d.controparte, dicitura, frequenza);
            if (!nucleo)
                continue;
            const numeroInCausale = numeroCitato(d.numero, dicitura);
            trovati.push({
                movimentoId: m.id,
                documentoId: d.id,
                importoCent: residuo,
                giorniDiScarto: scarto,
                numeroInCausale,
                motivo: (numeroInCausale ? `numero ${d.numero} nella causale, ` : '') +
                    `stesso importo, scadenza ${scadenza}` +
                    (scarto === 0 ? ' nello stesso giorno' : ` a ${scarto} ${scarto === 1 ? 'giorno' : 'giorni'}`) +
                    `, «${nucleo}» nella causale`,
            });
        }
        // Nessuna fattura dell'importo esatto: si guarda se la causale ne cita una
        // il cui importo differisce di pochi centesimi. Serve il numero **e** il
        // nome: il numero da solo, in una causale piena di codici, può capitare.
        if (trovati.length === 0) {
            for (const d of documenti) {
                if (d.tipo !== tipo || d.residuoCent <= 0)
                    continue;
                const differenza = Math.abs(d.residuoCent - residuo);
                if (differenza === 0 || differenza > TOLLERANZA_CON_NUMERO_CENT)
                    continue;
                if (!numeroCitato(d.numero, dicitura))
                    continue;
                const nucleo = nomeNellaCausale(d.controparte, dicitura, frequenza);
                if (!nucleo)
                    continue;
                const scarto = Math.min(Math.abs(giorniTra(d.dataScadenzaNominale ?? d.dataCassaAttesa, m.data)), Math.abs(giorniTra(d.dataCassaAttesa, m.data)));
                // Con il numero citato la data conta meno: si paga anche in ritardo.
                // Ma non all'infinito — un numero corto ricorre di anno in anno.
                if (scarto > 120)
                    continue;
                trovati.push({
                    movimentoId: m.id,
                    documentoId: d.id,
                    importoCent: Math.min(residuo, d.residuoCent),
                    giorniDiScarto: scarto,
                    numeroInCausale: true,
                    motivo: `numero ${d.numero} nella causale, «${nucleo}» nella causale, ` +
                        `importo diverso di ${differenza} ${differenza === 1 ? 'centesimo' : 'centesimi'}`,
                });
            }
        }
        // Un movimento che potrebbe chiudere due fatture diverse non si propone:
        // scegliere quale è esattamente la decisione che non spetta al programma.
        if (trovati.length === 1)
            candidature.push(trovati[0]);
    }
    // E nemmeno si propone un documento conteso da più movimenti, per lo stesso
    // motivo visto dall'altro lato.
    const quantiPerDocumento = new Map();
    for (const c of candidature)
        quantiPerDocumento.set(c.documentoId, (quantiPerDocumento.get(c.documentoId) ?? 0) + 1);
    return candidature
        .filter((c) => quantiPerDocumento.get(c.documentoId) === 1)
        .sort((x, y) => Number(y.numeroInCausale) - Number(x.numeroInCausale) || x.giorniDiScarto - y.giorniDiScarto);
}
/**
 * Come un movimento si spalma sui documenti che sta pagando.
 *
 * Un bonifico da 3.000 € che chiude tre fatture da 1.000: ogni documento prende
 * il minore fra il proprio residuo e quel che resta del movimento, e l'ordine è
 * quello in cui i documenti sono stati scelti. Quando il movimento non basta,
 * l'ultimo resta coperto solo in parte — e chi ha scelto ritrova la
 * ripartizione che aveva davanti, invece di una decisa da un ordinamento.
 *
 * Sta qui, e non dentro la rotta o dentro la schermata, perché la usano
 * **entrambe**: il conteggio che si aggiorna a ogni spunta dev'essere lo stesso
 * identico calcolo che poi viene scritto. Due implementazioni parallele dello
 * stesso conto prima o poi divergono, e a divergere sarebbe proprio il numero
 * che qualcuno ha guardato prima di premere.
 */
export function ripartisciSuDocumenti(residuoMovimentoCent, residuiDocumentoCent) {
    let resto = Math.max(0, residuoMovimentoCent);
    return residuiDocumentoCent.map((r) => {
        const quota = Math.min(Math.max(0, r), resto);
        resto -= quota;
        return quota;
    });
}
/**
 * Abbina i pagamenti che il gestionale conosce ai movimenti in banca.
 *
 * Il caso che conta è il **bonifico cumulativo**: un cliente paga sei fatture
 * con un'operazione sola, il gestionale le segna tutte pagate lo stesso giorno,
 * e in banca c'è un movimento solo che fa la somma. Su Erretielle, settembre
 * 2026: BRIANZA DATI sei fatture → 1.158,39 €, NEW WAY cinque → 579,50 €, OPNET
 * otto → 595,12 €. Cercando l'importo della singola fattura se ne trovano 8 su
 * 38; cercando la somma per controparte e per giorno, 26.
 *
 * Tre coincidenze insieme, come per le altre proposte: il gestionale dice che
 * è pagata e quando, la banca ha un movimento di quell'importo **esatto** nella
 * direzione giusta, e la data è vicina. Se due movimenti vanno bene allo stesso
 * modo non se ne sceglie uno: si lascia a chi guarda.
 *
 * Se la somma del gruppo non trova niente, si prova fattura per fattura: capita
 * che lo stesso giorno arrivino due bonifici separati.
 */
export function proponiDalGestionale(documenti, movimenti, finestraGiorni = 5) {
    const liberi = movimenti
        .map((m) => ({ ...m, residuoCent: Math.abs(m.importoCent) - m.allocatoCent }))
        .filter((m) => m.residuoCent > 0);
    const usati = new Set();
    /**
     * L'unico movimento che salda `importo`, o nessuno. Esatto al centesimo,
     * oppure — per una fattura sola — a pochi centesimi se la causale ne cita il
     * numero: vedi `TOLLERANZA_CON_NUMERO_CENT`.
     */
    const trova = (tipo, importoCent, pagatoIl, numero) => {
        const candidati = liberi
            .filter((m) => {
            if (usati.has(m.id))
                return false;
            if (tipo === 'ATTIVA' ? m.importoCent <= 0 : m.importoCent >= 0)
                return false;
            if (Math.abs(giorniTra(pagatoIl, m.data)) > finestraGiorni)
                return false;
            const differenza = Math.abs(m.residuoCent - importoCent);
            if (differenza === 0)
                return true;
            return (numero !== undefined &&
                differenza <= TOLLERANZA_CON_NUMERO_CENT &&
                numeroCitato(numero, m.dicitura.toUpperCase()));
        })
            .map((m) => ({ m, scarto: Math.abs(giorniTra(pagatoIl, m.data)) }))
            .sort((a, b) => a.scarto - b.scarto);
        if (candidati.length === 0)
            return null;
        // Due candidati alla stessa distanza sono un dubbio, non una scelta.
        if (candidati.length > 1 && candidati[0].scarto === candidati[1].scarto)
            return null;
        return candidati[0];
    };
    const gruppi = new Map();
    for (const d of documenti) {
        if (d.residuoCent <= 0)
            continue;
        const chiave = `${d.tipo}|${d.anagraficaId ?? d.controparte ?? d.id}|${d.pagatoIl}`;
        gruppi.set(chiave, [...(gruppi.get(chiave) ?? []), d]);
    }
    const proposte = [];
    const proponi = (docs, trovato) => {
        usati.add(trovato.m.id);
        const totaleCent = docs.reduce((t, d) => t + d.residuoCent, 0);
        const primo = docs[0];
        proposte.push({
            movimentoId: trovato.m.id,
            documenti: docs.map((d) => ({
                documentoId: d.id,
                numero: d.numero,
                // Con qualche centesimo di differenza si abbina il minore dei due: il
                // centesimo in più resta sul movimento, visibile, invece di sparire.
                importoCent: docs.length === 1 ? Math.min(d.residuoCent, trovato.m.residuoCent) : d.residuoCent,
            })),
            totaleCent,
            pagatoIl: primo.pagatoIl,
            giorniDiScarto: trovato.scarto,
            controparte: primo.controparte,
            motivo: (docs.length === 1
                ? `Pagata il ${primo.pagatoIl} secondo il gestionale`
                : `${docs.length} fatture pagate il ${primo.pagatoIl} secondo il gestionale, in un bonifico solo`) +
                (trovato.scarto === 0 ? '; in banca lo stesso giorno.' : `; in banca ${trovato.scarto} giorni di distanza.`),
        });
    };
    // Prima i gruppi più grandi: un movimento che salda sei fatture è una prova
    // più forte di uno che ne salda una, e non va consumato da una coincidenza.
    for (const docs of [...gruppi.values()].sort((a, b) => b.length - a.length)) {
        const totale = docs.reduce((t, d) => t + d.residuoCent, 0);
        const insieme = trova(docs[0].tipo, totale, docs[0].pagatoIl, docs.length === 1 ? docs[0].numero : undefined);
        if (insieme) {
            proponi(docs, insieme);
            continue;
        }
        if (docs.length === 1)
            continue;
        for (const d of docs) {
            const da = trova(d.tipo, d.residuoCent, d.pagatoIl, d.numero);
            if (da)
                proponi([d], da);
        }
    }
    return proposte;
}
// ———————————————————————————————————————————————————————————————
// Bonifici che saldano più fatture, senza il gestionale
/**
 * Cerca i bonifici che saldano più fatture dello stesso cliente o fornitore.
 *
 * Per le fatture che passano da Fatture in Cloud il raggruppamento lo dice il
 * gestionale (vedi `proponiDalGestionale`). Per le altre — le fatture storiche
 * importate dagli XML — no: resta solo la banca. Qui si cerca un movimento che
 * riporta il nome di una controparte e il cui importo è **esattamente la
 * somma** di alcune sue fatture aperte. Su Erretielle, sulle fatture vecchie:
 * TECNA 1.856,35 € = fatture 8 + 64, UNO COMMUNICATIONS 305,92 € = tre fatture.
 *
 * È la ricerca più esposta al caso, quindi la più prudente:
 *
 * - il nome nella causale deve indicare **una** controparte sola;
 * - la combinazione deve essere **unica**: con due fatture da 146,40 € fra le
 *   candidate, «quali due?» non ha risposta, e non si tira a indovinare;
 * - se una fattura sola salda il movimento, non è un cumulativo: è una proposta
 *   normale, o un dubbio;
 * - ogni fattura si usa una volta sola, andando per data: il primo bonifico che
 *   la salda se la prende.
 */
export function proponiCumulativi(movimenti, documenti, opzioni = {}) {
    const maxFatture = opzioni.maxFatture ?? 14;
    const indietro = opzioni.giorniIndietro ?? 240;
    const avanti = opzioni.giorniAvanti ?? 30;
    const frequenza = frequenzaParole(documenti.map((d) => d.controparte));
    const gruppi = new Map();
    for (const d of documenti) {
        if (d.residuoCent <= 0 || !d.anagraficaId || !d.controparte)
            continue;
        const k = `${d.tipo}|${d.anagraficaId}`;
        gruppi.set(k, [...(gruppi.get(k) ?? []), d]);
    }
    const usati = new Set();
    const proposte = [];
    for (const m of [...movimenti].sort((a, b) => a.data.localeCompare(b.data))) {
        const residuo = Math.abs(m.importoCent) - m.allocatoCent;
        if (residuo <= 0)
            continue;
        const tipo = m.importoCent > 0 ? 'ATTIVA' : 'PASSIVA';
        const dicitura = m.dicitura.toUpperCase();
        const agganciati = [...gruppi.entries()].filter(([k, docs]) => k.startsWith(`${tipo}|`) && nomeNellaCausale(docs[0].controparte, dicitura, frequenza) !== null);
        // Un nome che aggancia due controparti non dice di chi è il bonifico.
        if (agganciati.length !== 1)
            continue;
        const docs = agganciati[0][1];
        const candidate = docs
            .filter((d) => {
            if (usati.has(d.id))
                return false;
            const giorni = giorniTra(d.dataCassaAttesa, m.data);
            return giorni <= indietro && giorni >= -avanti;
        })
            .sort((a, b) => b.dataCassaAttesa.localeCompare(a.dataCassaAttesa))
            .slice(0, maxFatture);
        if (candidate.length < 2)
            continue;
        // Una fattura sola che basta: non è un cumulativo.
        if (candidate.some((d) => d.residuoCent === residuo))
            continue;
        let trovata = null;
        let quante = 0;
        for (let maschera = 1; maschera < 1 << candidate.length && quante < 2; maschera++) {
            if ((maschera & (maschera - 1)) === 0)
                continue; // almeno due fatture
            let somma = 0;
            for (let i = 0; i < candidate.length; i++)
                if (maschera & (1 << i))
                    somma += candidate[i].residuoCent;
            if (somma === residuo) {
                trovata = maschera;
                quante += 1;
            }
        }
        if (quante !== 1 || trovata === null)
            continue;
        const scelte = candidate.filter((_, i) => trovata & (1 << i));
        for (const d of scelte)
            usati.add(d.id);
        const piuRecente = scelte.reduce((a, d) => (d.dataCassaAttesa > a ? d.dataCassaAttesa : a), scelte[0].dataCassaAttesa);
        proposte.push({
            movimentoId: m.id,
            documenti: scelte.map((d) => ({ documentoId: d.id, numero: d.numero, importoCent: d.residuoCent })),
            totaleCent: residuo,
            pagatoIl: m.data,
            giorniDiScarto: Math.abs(giorniTra(piuRecente, m.data)),
            controparte: docs[0].controparte,
            motivo: `il bonifico è esattamente la somma di ${scelte.length} fatture aperte di ` +
                `«${nomeNellaCausale(docs[0].controparte, dicitura, frequenza)}», e nessun'altra combinazione lo dà`,
        });
    }
    return proposte;
}
//# sourceMappingURL=abbinamenti.js.map